import { Auth } from 'aws-amplify';
import Axios from 'axios';

import { CHARACTER_MANAGER, CHARACTER_TEMPLATE_MANAGER } from '../apis';
import { debugSession, getJwtSummary } from '../utils/sessionDebug';

const generateConfig = (jwtToken, apiToken) => {
	return {
		headers: {
			Accept: '*/*',
			Authorization: jwtToken,
			'content-type': '',
			'X-API-KEY': apiToken,
		},
		timeout: 10000,
	};
};

const normalizeElements = (elements, sectionType) => {
	return elements.map((el) => {
		return {
			...el,
			value: sectionType === 'counter' && el.value != null ? JSON.stringify(el.value) : el.value,
		};
	});
};

const normalizeTemplateValues = (template) => {
	

	return {
		...template,
		sections: template.sections.map((section) => ({
			...section,
			elements: normalizeElements(section.elements, section.type),
		})),
	};
};

const normalizeCharacterValues = (character) => {
	return {
		...character,
		template: normalizeTemplateValues(character.template),
		sections: character.sections.map((section) => {
			return {
				...section,
				elements: normalizeElements(section.elements, section.type),
			};
		}),
	};
};

const parseSectionElements = (section) => {
	return section.elements?.map((el) => {
		let parsedValue = el.value ?? '';

		if (section.type === 'counter') {
			try {
				parsedValue = JSON.parse(el.value);
			} catch (e) {
				console.warn(`Failed to parse Counter value for ${el.label}:`, el.value);
			}
		}

		return {
			...el,
			value: parsedValue,
		};
	});
};

const parseTemplateValues = (template) => {
	

	return {
		...template,
		sections: template.sections.map((section) => ({
			...section,
			elements: parseSectionElements(section),
		})),
	};
};

const parseCharacterValues = (character) => {
	return {
		...character,
		template: parseTemplateValues(character.template),
		sections: character.sections.map((section) => {
			return {
				...section,
				elements: parseSectionElements(section),
			};
		}),
	};
};

const characterApis = CHARACTER_MANAGER[process.env.REACT_APP_API_ENV];
const characterTemplateApis = CHARACTER_TEMPLATE_MANAGER[process.env.REACT_APP_API_ENV];

export const createCharacter = async (character) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(currentSession.getIdToken().getJwtToken(), characterApis.token);
	const currentUserInfo = await Auth.currentUserInfo();
	const userId = currentUserInfo.username;
	character.owner = userId;
	try {
		const normalizedCharacter = normalizeCharacterValues(character);
		const response = await Axios.post(characterApis.urls.createCharacter, normalizedCharacter, config);
		return parseCharacterValues(JSON.stringify(response.data));
	} catch (error) {
		console.log('Error creating character', error);
		throw error;
	}
};

export const getCharacter = async (characterId) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(currentSession.getIdToken().getJwtToken(), characterApis.token);

	try {
		const response = await Axios.get(characterApis.urls.getCharacter(characterId), config);
		const { data } = JSON.parse(response.data.body);
		debugSession('characterRequests', 'get-character-success', {
			characterId,
			alienSystemState: data?.systemState?.alien || null,
		});
		return parseCharacterValues(data);
	} catch (error) {
		console.log('Error getting character', error);
		throw error;
	}
};

export const getAllCharacters = async ({ size, next }) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(
		currentSession.getIdToken().getJwtToken(),
		characterTemplateApis.token
	);
	try {
		const response = await Axios.get(
			characterApis.urls.getAllCharacters,
			config
		);
		return response.data;
	} catch (error) {
		console.log('Error getting all characters', error);
		throw error;
	}
};

export const deleteCharacter = async (characterId) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(currentSession.getIdToken().getJwtToken(), characterApis.token);

	try {
		const response = await Axios.delete(
			characterApis.urls.deleteCharacter(characterId),
			config
		);
		return response.data;
	} catch (error) {
		console.log('Error deleting character', error);
		throw error;
	}
};

export const updateCharacter = async (characterId, updatedCharacter) => {
	debugSession('characterRequests', 'update-character-start', {
		characterId,
		sectionCount: updatedCharacter?.sections?.length || 0,
		alienSystemState: updatedCharacter?.systemState?.alien || null,
	});
	const currentSession = await Auth.currentSession();
	debugSession('characterRequests', 'update-character-session', {
		characterId,
		jwt: getJwtSummary(currentSession.getIdToken().getJwtToken()),
	});
	const config = generateConfig(currentSession.getIdToken().getJwtToken(), characterApis.token);

	try {
		const normalizedCharacter = normalizeCharacterValues(updatedCharacter);
		const response = await Axios.put(
			characterApis.urls.updateCharacter(characterId),
			normalizedCharacter,
			config
		);
		debugSession('characterRequests', 'update-character-success', {
			characterId,
			status: response?.status || null,
			alienSystemState: updatedCharacter?.systemState?.alien || null,
		});
		return response.data;
	} catch (error) {
		debugSession('characterRequests', 'update-character-error', {
			characterId,
			message: error?.message || null,
			status: error?.response?.status || null,
		});
		console.log('Error updating character', error);
		throw error;
	}
};

export const createCharacterTemplate = async (template) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(
		currentSession.getIdToken().getJwtToken(),
		characterTemplateApis.token
	);
	const normalizedTemplate = normalizeTemplateValues(template);
	try {
		const response = await Axios.post(
			characterTemplateApis.urls.createTemplate,
			normalizedTemplate,
			config
		);
		return parseTemplateValues(JSON.parse(response.data.body).data.template);
	} catch (error) {
		console.log('Error creating character template', error);
		throw error;
	}
};

export const getCharacterTemplate = async (templateId) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(
		currentSession.getIdToken().getJwtToken(),
		characterTemplateApis.token
	);

	try {
		const response = await Axios.get(
			characterTemplateApis.urls.getTemplate(templateId),
			config
		);
		const { data } = JSON.parse(response.data.body);
		return parseTemplateValues(data);
	} catch (error) {
		console.log('Error getting character template', error);
		throw error;
	}
};

export const getAllCharacterTemplates = async ({ size, next }) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(
		currentSession.getIdToken().getJwtToken(),
		characterTemplateApis.token
	);

	try {
		const response = await Axios.get(
			characterTemplateApis.urls.getAllTemplates,
			config
		);
		return response.data;
	} catch (error) {
		console.log('Error getting all character templates', error);
		throw error;
	}
};

export const deleteCharacterTemplate = async (templateId) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(
		currentSession.getIdToken().getJwtToken(),
		characterTemplateApis.token
	);

	try {
		const response = await Axios.delete(
			characterTemplateApis.urls.deleteTemplate(templateId),
			config
		);
		return response.data;
	} catch (error) {
		console.log('Error deleting character template', error);
		throw error;
	}
};

export const updateCharacterTemplate = async (updatedTemplate) => {
	const currentSession = await Auth.currentSession();
	const config = generateConfig(
		currentSession.getIdToken().getJwtToken(),
		characterTemplateApis.token
	);

	const normalizedTemplate = normalizeTemplateValues(updatedTemplate);

	try {
		const response = await Axios.put(
			characterTemplateApis.urls.updateTemplate(updatedTemplate.ID),
			normalizedTemplate,
			config
		);
		return parseTemplateValues(JSON.parse(response.data.body).data.template);
	} catch (error) {
		console.log('Error updating character template', error);
		throw error;
	}
};
